import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import authService from '../../services/auth.service';
import { getAuthErrorMessage } from '../../utils/authErrorHandler';
import AuthShell from './AuthShell';
import AuthSubmitButton from './AuthSubmitButton';

const EmailVerification: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const email = location.state?.email || '';
  const message =
    location.state?.message || t('auth:emailVerification.checkEmailMessage');
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const isVerifyingRef = useRef(false);
  const isResendingRef = useRef(false);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    if (isVerifyingRef.current) return;
    isVerifyingRef.current = true;
    setIsLoading(true);
    setError('');
    try {
      await authService.verifyEmail(verificationToken);
      setSuccess(true);
      toast.success(t('auth:emailVerification.verifiedMessage'));
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err, t, 'emailVerification');
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      isVerifyingRef.current = false;
    }
  };

  const handleResendVerification = async () => {
    if (isResendingRef.current) return;
    if (!email) {
      setError(t('auth:emailVerification.emailNotFound'));
      return;
    }
    isResendingRef.current = true;
    setResending(true);
    setError('');
    try {
      const result = await authService.resendVerification(email);
      if (result.success) {
        setError('');
        toast.success(t('auth:emailVerification.resendSuccess'));
      }
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err, t, 'emailVerification');
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setResending(false);
      isResendingRef.current = false;
    }
  };

  // ---------- Loading state (token in URL is being verified) ----------
  if (isLoading) {
    return (
      <AuthShell
        title={t('auth:emailVerification.verifyingTitle')}
        subtitle={t('auth:emailVerification.verifyingMessage')}
      >
        <div className="flex justify-center" aria-hidden="true">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-sky-400" />
        </div>
      </AuthShell>
    );
  }

  // ---------- Success state ----------
  if (success) {
    return (
      <AuthShell title={t('auth:emailVerification.verifiedTitle')}>
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/30">
            <CheckCircleIcon className="h-6 w-6 text-emerald-400" />
          </div>
          <p className="mt-6 text-base text-white/70">
            {t('auth:emailVerification.verifiedMessage')}
          </p>
        </div>
      </AuthShell>
    );
  }

  // ---------- Error state (token verification failed) ----------
  if (token && error) {
    return (
      <AuthShell title={t('auth:emailVerification.failedTitle')}>
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 border border-red-500/30">
            <ExclamationCircleIcon className="h-6 w-6 text-red-400" />
          </div>
          <p className="mt-6 text-sm text-red-400">{error}</p>
          <div className="mt-8">
            <Link
              to="/signup"
              className="text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black text-sm font-medium"
            >
              {t('auth:emailVerification.backToSignUp')} →
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  // ---------- Idle state (waiting for the user to click the email link) ----------
  return (
    <AuthShell title={t('auth:emailVerification.title')}>
      <div className="flex flex-col items-center text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/10 border border-sky-500/30 mb-6">
          <EnvelopeIcon className="h-6 w-6 text-sky-400" />
        </div>
        <p className="text-base text-white/70">{message}</p>
        {email && (
          <p className="mt-3 text-base text-white/60">
            {t('auth:emailVerification.sentToEmail')}{' '}
            <span className="text-white">{email}</span>
          </p>
        )}
        <p className="mt-3 text-sm text-white/50">
          {t('auth:emailVerification.clickLinkMessage')}
        </p>

        {error && (
          <p className="mt-4 text-sm text-red-400">{error}</p>
        )}

        <div className="mt-10 w-full space-y-4">
          <p className="text-sm text-white/50">
            {t('auth:emailVerification.didntReceive')}
          </p>
          <AuthSubmitButton
            type="button"
            variant="secondary"
            isLoading={resending}
            loadingLabel={t('auth:emailVerification.resendingButton')}
            onClick={handleResendVerification}
          >
            {t('auth:emailVerification.resendButton')}
          </AuthSubmitButton>
          <Link
            to="/signin"
            className="block text-sm text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black font-medium"
          >
            {t('auth:emailVerification.backToSignIn')} →
          </Link>
        </div>
      </div>
    </AuthShell>
  );
};

export default EmailVerification;
